import { CORPUS_QA, PAPER_FINDER } from 'configs/NoraVariants';
import { IconAi2Logo } from '@/components/icons/IconAi2Logo';
import { optAppConfig } from '@/utils/config';

import {
  Box,
  Card,
  CardActions,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import styled from 'styled-components';
import VarnishTheme from '@allenai/varnish-theme';

const cardData = [
  {
    title: 'Discovery',
    description:
      'Find papers in your field, from popular ones to more niche and hard-to-find works.',
    icon: <SearchIcon />,
    link: { text: 'Find Papers', url: 'https://paperfinder.allen.ai' },
    width: 271,
    variant: PAPER_FINDER,
  },
  {
    title: 'Synthesis',
    description:
      'Ask a question and get a comprehensive answer that synthesizes and cites multiple papers.',
    icon: <AssignmentOutlinedIcon />,
    link: {
      text: 'Get Insights',
      url: 'https://qa.allen.ai',
    },
    width: 271,
    variant: CORPUS_QA,
  },
  {
    title: 'More Coming Soon',
    description:
      'We’re actively developing new and exciting AI tools for scientists. Stay tuned!',
    icon: <IconAi2Logo fill={VarnishTheme.color['cream-100'].value} />,
    link: {
      text: 'Subscribe to Ai2 news',
      url: 'https://share.hsforms.com/1uJkWs5aDRHWhiky3aHooIg3ioxm',
    },
    width: 181,
  },
];

export const CrossLinks = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const noraExperienceVariant = optAppConfig((_) => _.experience.variant);

  const filteredCardData = isSmallScreen ? cardData.slice(0, -1) : cardData;

  return (
    <Stack spacing={1.5} mt={1}>
      <Title variant="h4">Scholarly tools</Title>
      <CardsWrapper>
        {filteredCardData.map(
          ({ title, description, icon, link, width, variant }, index) => {
            const isCurrentVariant = noraExperienceVariant === variant;
            const cardInnerContent = (
              <CardContentWrapper>
                <IconTitleWrapper $isSmallScreen={isSmallScreen}>
                  <CircleIcon $isCurrentVariant={isCurrentVariant}>
                    {icon}
                  </CircleIcon>
                  <TitleText>{title}</TitleText>
                </IconTitleWrapper>
                <DescriptionText>{description}</DescriptionText>
              </CardContentWrapper>
            );
            return (
              <StyledCard
                key={index}
                $isSmallScreen={isSmallScreen}
                width={width}
                $isCurrentVariant={isCurrentVariant}
              >
                {!isCurrentVariant && link ? (
                  <Link
                    href={link.url}
                    underline="none"
                    rel="noopener"
                    target="_blank"
                  >
                    {cardInnerContent}
                  </Link>
                ) : (
                  cardInnerContent
                )}
                {!isCurrentVariant && !isSmallScreen && link && (
                  <StyledCardAction>
                    <StyledLink
                      href={link.url}
                      underline="none"
                      rel="noopener"
                      target="_blank"
                    >
                      <span>{link.text}</span>
                      <ChevronRightIcon />
                    </StyledLink>
                  </StyledCardAction>
                )}
              </StyledCard>
            );
          },
        )}
      </CardsWrapper>
    </Stack>
  );
};

const CardContentWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: flex-start;
`;

const CardsWrapper = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing(2)};
  justify-content: flex-start;
  max-width: 100%;
  @media (max-width: ${({ theme }) => theme.breakpoints.values.sm}px) {
    flex-direction: column;
  }
`;

const CircleIcon = styled(Box)<{
  $isCurrentVariant: boolean;
}>`
  align-items: center;
  background-color: ${({ theme }) => theme.color['teal-100'].hex};
  border-radius: 50%;
  color: ${({ theme }) => theme.color['cream-100'].hex};
  display: flex;
  height: 36px;
  justify-content: center;
  width: 36px;

  svg {
    fill: ${({ $isCurrentVariant, theme }) =>
      $isCurrentVariant
        ? 'rgba(15, 203, 140, 1)'
        : theme.color['cream-100'].hex};
  }
`;

const DescriptionText = styled.p`
  color: rgba(250, 242, 233, 0.8);
  font-family: ${({ theme }) => theme.font.family.body};
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
`;

const IconTitleWrapper = styled(Box)<{ $isSmallScreen: boolean }>`
  align-items: ${({ $isSmallScreen }) =>
    $isSmallScreen ? 'center' : 'flex-start'};
  display: flex;
  flex-direction: ${({ $isSmallScreen }) =>
    $isSmallScreen ? 'row' : 'column'};
  gap: 12px;
`;

const StyledCard = styled(Card)<{
  $isSmallScreen: boolean;
  width: number;
  $isCurrentVariant: boolean;
}>`
  &.MuiCard-root {
    align-items: flex-start;
    background: rgba(250, 242, 233, 0.04);
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    padding: ${({ theme }) => theme.spacing(2)};
    width: ${({ $isSmallScreen, width }) =>
      $isSmallScreen ? '100%' : `${width}px`};

    /* Apply additional styles based on the variant */
    ${({ $isCurrentVariant }) =>
      $isCurrentVariant &&
      `
        border-radius: 2px;
        border: 1px solid rgba(250, 242, 233, 0.20);
      `}
  }
`;
const StyledCardAction = styled(CardActions)`
  &.MuiCardActions-root {
    display: flex;
    justify-content: flex-start;
    padding: 0;
  }
`;

const StyledLink = styled(Link)`
  align-items: center;
  display: flex;

  span {
    color: ${({ theme }) => theme.color['green-100'].hex};
    font-size: 14px;
  }

  svg {
    color: ${({ theme }) => theme.color['green-100'].hex};
    margin-left: 5px;
  }
`;

const Title = styled(Typography)`
  color: ${({ theme }) => theme.color['cream-100'].hex};
`;

const TitleText = styled.p`
  color: ${({ theme }) => theme.color['cream-100'].hex};
  font-family: ${({ theme }) => theme.font.family.heading};
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 0px;
`;
